/**
 * Generates a prepared class name string based on the provided class name and special properties.
 *
 * @param ambHeightCalc - property for Ambassador height calculations
 * @param tag - Tag.
 * @param ambVoyageTotals -  property for Ambassador totals calculations
 * @param className - An optional string representing the base class name.
 * @param colIndex - Column index for engine selects
 * @param getEngineSelectValue - Function to get engine select value by colIndex
 * @returns A formatted class name string.
 */
export const getPreparedClassname = (
  ambHeightCalc: string | undefined,
  tag: string | undefined,
  ambVoyageTotals: string | undefined,
  amlGroup: string | undefined,
  amlCalc: string | undefined,
  className: string | undefined,
  domId?: string,
  colIndex?: number,
  rowIdx?: number,
  getEngineSelectValue?: (colIndex: number) => string,
) => {
  if (ambHeightCalc) return "ambHeightCalc";
  if (tag === "tripLog") return "tripLog";
  if (tag === "add") {
    if (colIndex !== undefined && getEngineSelectValue) {
      const engineValue = getEngineSelectValue(colIndex);

      if (engineValue) {
        return `oilAdded${engineValue.toUpperCase()}`;
      }
    }
    return `oilAdded`;
  }
  if (["oilUsed1", "oilUsed2", "oilUsed3"].includes(tag ?? ""))
    return `oilUsed ${tag}`;
  if (tag === "FWTotalChange") return "FWTotalChange";
  if (ambVoyageTotals == "totalVoyage") return "totalVoyage";
  if (amlGroup) className = `${amlGroup}_add`;
  if (amlCalc) className += ` amlPerc amlPerc_${rowIdx}`;
  if (
    ["nameofsea", "caponboard"].includes(tag ?? "") ||
    tag?.includes("seamen_")
  )
    return tag;
  return className;
};

/**
 * Generates a class name string based on the provided class name and row index.
 *
 * @param className or Tag - An optional string representing the base class name.
 * @param rowIdx - An optional string or number representing the row index.
 * @returns A formatted class name string.
 */
export const getClassName = (className?: string, rowIdx?: string | number) => {
  if (className === "totalVoyage") return className;

  let selector = className?.includes("logColumns")
    ? `logColumns logColumns_${rowIdx}`
    : className === "tripLog" || className === "SOG"
      ? `${className} ${className}_${rowIdx}`
      : className;
  if (className === "ambHeightCalc") {
    selector += `_${rowIdx}`;
  } else if (className === "logResuls") {
    selector += ` logResuls_${rowIdx}`;
  }

  if (["valorA", "valorB"].includes(className ?? ""))
    return ` valor ${className}_${rowIdx}`;

  return selector ?? "";
};
/**
 * Generates a feature key string based on the provided class name and ID.
 *
 * @param className - An optional string representing the base class name.
 * @param id - An optional string or number representing the ID.
 * @returns A formatted feature key string.
 */
export const getFeatureKey = (className?: string, id?: string | number) => {
  if (className === "24lock" || className === "ambHeightCalc") {
    return `${className}_${id}`;
  }

  let result = `${className}_sum`;

  if (id !== undefined && className) {
    if (className === "logColumns") {
      result = `logColumns_diff_${id}`;
    } else {
      result = `${className}_${id}`;
    }
  }
  return result;
};
/**
 * Retrieves the type of ambassador total based on the provided data object.
 *
 * @param data - An object containing optional properties for different ambient totals.
 * @returns A string representing the key of the first matching ambient total type.
 */
export const getAmbTotalType = (data: {
  ambVoyageTotals?: string;
  ambSteamingTotals?: string;
  ambSpeedTotals?: string;
}) => {
  const keys = Object.keys(data);
  return keys.find(k => data[k as keyof typeof data]);
};
/**
 * Retrieves the matched class name based on the provided type.
 *
 * @param type - A string representing the type of ambient total.
 * @returns A string representing the matched class name.
 */
export const getMatchedClassname = (type: string) => {
  switch (type) {
    case "ambVoyageTotals":
      return "tripLog";
    case "ambSteamingTotals":
      return "tripLog";
    case "ambSpeedTotals":
      return "SOG";
    default:
      return "";
  }
};
